import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  CardTitle,
} from "reactstrap"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import {
  getLottieLoadingOptions,
  getLottieNothingFound,
  getOperatorByIccid,
  showToast,
  stringSorter,
} from "utils/general"
import { Table as AntdTable, Input as AntdInput, Select, AutoComplete, Divider, DatePicker, Popconfirm, Drawer, } from "antd"
import { tableDefaultHeight } from "constants/layout"
import { useFormik } from "formik"
import Lottie from "react-lottie"
import { deleteReturnsEntry, getReturnsData, updateReturnsEntry } from "helpers/backend_helper"
import dayjs from "dayjs"
import { isEqual } from "lodash"
import SelectCarephone from "pages/Services/Integrations/components/SelectCarephone"
import SelectTracker from "pages/Services/Integrations/components/SelectTracker"
import SelectVideoDevice from "pages/Services/Integrations/components/SelectVideoDevice"
import SelectPindora from "pages/Services/Integrations/components/SelectPindora"
import SelectGateway from "pages/Services/Integrations/components/SelectGateway"
import { Exception } from "sass"
import { getReturnsReasonText } from "./ReturnsManagement"
const { TextArea } = AntdInput;

const ReturnsForm = props => {
  const [data, setData] = useState()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [refreshConfirm, setRefreshConfirm] = useState(false)

  const [returnsItems, setReturnsItems] = useState([])
  const [deliveryItems, setDeliveryItems] = useState([])
  const [reasonOptions, setReasonOptions] = useState([])
  const [itemOptions, setItemOptions] = useState([])
  const [orgOptions, setOrgOptions] = useState([])

  const [suggestions, setSuggestions] = useState([])
  const [selectedCarephone, setSelectedCarephone] = useState()
  const [selectedTracker, setSelectedTracker] = useState()
  const [selectedVideoDevice, setSelectedVideoDevice] = useState()
  const [selectedSmartlock, setSelectedSmartlock] = useState()
  const [selectedGateway, setSelectedGateway] = useState()

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()

    let opts = [
      { label: props.t("Home gateway"), value: "home_gw" },
      { label: props.t("At home set"), value: "home_set" },
      { label: props.t("Home sensor"), value: "home_node" },
      { label: props.t("Home accessory"), value: "home_accessory" },
      { label: props.t("Tracker"), value: "tracker_device" },
      { label: props.t("Tracker accessory"), value: "tracker_accessory" },
      { label: props.t("Safety wristwatch"), value: "wristwatch_device" },
      { label: props.t("Safety wristwatch accessory"), value: "wristwatch_accessory" },
      { label: props.t("Carephone"), value: "carephone_main" },
      { label: props.t("Carephone accessory"), value: "carephone_accessory" },
      { label: props.t("Video device"), value: "video_device" },
      { label: props.t("Video accessory"), value: "video_accessory" },
      { label: props.t("Smartlock"), value: "smartlock_device" },
      { label: props.t("Smartlock accessory"), value: "smartlock_accessory" },
      { label: props.t("Batteries"), value: "batteries" },
      { label: props.t("Other"), value: "other" },
    ]
    opts.sort((a, b) => a.label.localeCompare(b.label))
    setItemOptions(opts)

    let opts2 = [
      { label: props.t(getReturnsReasonText(1)), value: 1 },
      { label: props.t(getReturnsReasonText(2)), value: 2 },
      { label: props.t(getReturnsReasonText(3)), value: 3 },
      { label: props.t(getReturnsReasonText(4)), value: 4 },
      { label: props.t(getReturnsReasonText(5)), value: 5 },
      { label: props.t(getReturnsReasonText(6)), value: 6 },
      { label: props.t(getReturnsReasonText(7)), value: 7 },
      { label: props.t(getReturnsReasonText(8)), value: 8 },
      { label: props.t(getReturnsReasonText(9)), value: 9 },
    ]
    opts2.sort((a, b) => a.label.localeCompare(b.label))
    setReasonOptions(opts2)

    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    setData()
    getData()
    resetSelections()
    returnedItemsValidation.resetForm()
    deliveryItemsValidation.resetForm()
  }, [props.id])

  // Get data
  const getData = () => {
    if (props.id && !loading) {
      setLoading(true)
      setReturnsItems([])
      setDeliveryItems([])
      validation.resetForm()
      getReturnsData(abortRef.current, props.id).then(result => {
        if (result.statusCode == 200 && result.entry) {
          setData(result.entry)
          if (result.entry?.returns_items) {
            try {
              let temp = JSON.parse(result.entry.returns_items)
              setReturnsItems(temp)
            }
            catch (err) {
              console.log(err)
            }
          }
          if (result.entry?.delivery_items) {
            try {
              let temp = JSON.parse(result.entry.delivery_items)
              setDeliveryItems(temp)
            }
            catch (err) {
              console.log(err)
            }
          }

          // Organization suggestion
          if (result.organization_list) {
            try {
              let temp = []
              for (const element of result.organization_list) {
                temp.push({ value: element })
              }
              setOrgOptions(temp)
              setSuggestions(temp)
            }
            catch (err) {
              console.log(err)
            }
          }
        }
        else {
          setData()
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        setLoading(false)
      })
    }
  }

  // Delete entry
  const deleteEntry = () => {
    if (data?.id && !loading && !saving && !deleting) {
      setDeleting(true)
      deleteReturnsEntry(abortRef.current, data.id).then(result => {
        if (result.statusCode == 200) {
          getData()
          showToast(props.t("Deleted"), "success", 3000, "toast-bottom-right")
        }
        else throw Exception("Unexpected result")
      }).catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error", 3000, "toast-bottom-right")
      }).finally(() => {
        setDeleting(false)
      })
    }
  }

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      organization: data?.organization || "",
      customer: data?.customer || "",
      contacts: data?.contacts || "",
      source_type: data?.source_type || 0,
      source_text: data?.source_text || "",
      additional_info: data?.additional_info || "",
      informed_at: data?.informed_at ? dayjs(data.informed_at) : null,
      returns_status: data?.returns_status || 0,
      delivery_status: data?.delivery_status || 0,
      returns_tracking_code: data?.returns_tracking_code || "",
      returns_reason: data?.returns_reason || null,
      returns_missing: data?.returns_missing || 0,
      returns_handled_at: data?.returns_handled_at ? dayjs(data.returns_handled_at) : null,
      returns_additional: data?.returns_additional || "",
      invoicing: data?.invoicing || null,
      invoicing_content: data?.invoicing_content || "",
      invoicing_ends_at: data?.invoicing_ends_at ? dayjs(data.invoicing_ends_at) : null,
      delivery_tracking_code: data?.delivery_tracking_code || "",
      delivery_sent_at: data?.delivery_sent_at ? dayjs(data.delivery_sent_at) : null,
      elisa_register: data?.elisa_register || null,
      delivery_additional: data?.delivery_additional || "",
      highlight: data?.highlight || 0,
      highlight_reason: data?.highlight_reason || "",
    },
    onSubmit: values => {
      if (!loading && !saving && !deleting && props.id) {

        if (values.returns_reason == null) values.returns_reason = 0
        if (values.invoicing == null) values.invoicing = 0
        if (values.elisa_register == null) values.elisa_register = 0

        /*if (values.source_type == 1 && values.source_text.startsWith("http")) {
          try {
            values.source_text = new URL(values.source_text).searchParams.get("id")
          }
          catch (e) { console.log(e) }
        }*/
        if (values.source_type == 1) {
          values.source_text = values.source_text.replace(/#/g, "")
        }

        let changedValues = {}
        for (const key in values) {
          if (!isEqual(validation.initialValues[key], values[key])) {
            changedValues[key] = values[key]
          }
        }

        if ("invoicing_ends_at" in changedValues && changedValues.invoicing_ends_at == null) {
          changedValues.invoicing_ends_at = 0
        }
        if ("returns_handled_at" in changedValues && changedValues.returns_handled_at == null) {
          changedValues.returns_handled_at = 0
        }
        if ("delivery_sent_at" in changedValues && changedValues.delivery_sent_at == null) {
          changedValues.delivery_sent_at = 0
        }

        // Check if items have changed
        let returnsEqual = false, deliveryEqual = false
        try {
          returnsEqual = isEqual(returnsItems, JSON.parse(data.returns_items))
          deliveryEqual = isEqual(deliveryItems, JSON.parse(data.delivery_items))
        }
        catch (err) {
          console.log(err)
        }
        if (!returnsEqual) changedValues.returns_items = JSON.stringify(returnsItems)
        if (!deliveryEqual) changedValues.delivery_items = JSON.stringify(deliveryItems)

        //console.log("Changed: ", changedValues)

        setSaving(true)
        updateReturnsEntry(abortRef.current, props.id, changedValues).then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Changes saved"), "success", 3000, "toast-bottom-right")
            getData()
          }
          else throw new Error("Unexpected result")
        }).catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error", 3000, "toast-bottom-right")
        }).finally(() => {
          setSaving(false)
        })
      }
    },
  })

  // Returned items validation
  const returnedItemsValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: null,
      info: "",
    },
    onSubmit: values => {
      let temp = structuredClone(values)
      const crypto = globalThis.crypto || globalThis.msCrypto
      temp.id = "" + crypto.randomUUID()

      let deviceData = getDeviceData(values.type)
      if (deviceData.device_id) temp.device_id = deviceData.device_id
      if (deviceData.info) temp.info = deviceData.info

      setReturnsItems(prev => [temp, ...prev])
      returnedItemsValidation.setFieldValue("info", "")
      resetSelections()
    },
  })

  // Delivery items validation
  const deliveryItemsValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: null,
      info: "",
    },
    onSubmit: values => {
      let temp = structuredClone(values)
      const crypto = globalThis.crypto || globalThis.msCrypto
      temp.id = "" + crypto.randomUUID()

      let deviceData = getDeviceData(values.type)
      if (deviceData.device_id) temp.device_id = deviceData.device_id
      if (deviceData.info) temp.info = deviceData.info

      setDeliveryItems(prev => [temp, ...prev])
      deliveryItemsValidation.setFieldValue("info", "")
      resetSelections()
    },
  })

  useEffect(() => {
    resetSelections()
    returnedItemsValidation.setFieldValue("info", "")
  }, [returnedItemsValidation.values.type])

  useEffect(() => {
    resetSelections()
    deliveryItemsValidation.setFieldValue("info", "")
  }, [deliveryItemsValidation.values.type])

  // Get device ID and info
  const getDeviceData = (type) => {
    let temp = {}
    if (type == "carephone_main" && selectedCarephone) {
      temp.info = ""
      if (selectedCarephone.type) temp.info = selectedCarephone.type + ", "
      temp.info = temp.info + "S/N:" + selectedCarephone.label
      if (selectedCarephone.iccid) {
        let operator = getOperatorByIccid(selectedCarephone.iccid)
        if (operator) {
          temp.info = temp.info + ", SIM:" + operator
        }
      }
      temp.device_id = selectedCarephone.value
    }
    else if ((type == "tracker_device" || type == "wristwatch_device") && selectedTracker) {
      temp.info = ""
      if (selectedTracker.type) temp.info = selectedTracker.type + ", "
      if (selectedTracker.serial) temp.info = temp.info + "S/N:" + selectedTracker.serial + ", "
      if (selectedTracker.imei) temp.info = temp.info + "IMEI:" + selectedTracker.imei + ", "
      if (selectedTracker.iccid) {
        let operator = getOperatorByIccid(selectedTracker.iccid)
        if (operator) {
          temp.info = temp.info + "SIM:" + operator
        }
      }
      temp.device_id = selectedTracker.value
    }
    else if (type == "video_device" && selectedVideoDevice) {
      temp.info = ""
      if (selectedVideoDevice.model) temp.info = selectedVideoDevice.model + ", "
      temp.info = temp.info + "S/N:" + selectedVideoDevice.value
      if (selectedVideoDevice.imei) temp.info = temp.info + ", IMEI:" + selectedVideoDevice.imei
      if (selectedVideoDevice.iccid) {
        let operator = getOperatorByIccid(selectedVideoDevice.iccid)
        if (operator) {
          temp.info = temp.info + ", SIM:" + operator
        }
      }
      temp.device_id = selectedVideoDevice.value
    }
    else if (type == "smartlock_device" && selectedSmartlock) {
      temp.info = "S/N:" + selectedSmartlock.name
      if (selectedSmartlock.imei) temp.info = temp.info + ", IMEI:" + selectedSmartlock.imei
      if (selectedSmartlock.iccid) {
        let operator = getOperatorByIccid(selectedSmartlock.iccid)
        if (operator) {
          temp.info = temp.info + ", SIM:" + operator
        }
      }
      temp.device_id = selectedSmartlock.value
    }
    else if ((type == "home_gw" || type == "home_set") && selectedGateway) {
      temp.info = "S/N:" + selectedGateway.serial
      if (selectedGateway.iccid) {
        let operator = getOperatorByIccid(selectedGateway.iccid)
        if (operator) {
          temp.info = temp.info + ", SIM:" + operator
        }
      }
      temp.device_id = selectedGateway.value
    }
    return temp
  }

  // Reset every device selections
  const resetSelections = () => {
    setSelectedCarephone()
    setSelectedSmartlock()
    setSelectedTracker()
    setSelectedVideoDevice()
    setSelectedGateway()
  }

  // Items table columns
  const devicesColumns = [
    {
      dataIndex: "type",
      title: props.t("Type"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "type", sortOrder)
      },
      render: (cell, row) => {
        switch (cell) {
          case "carephone_main": return <><i className="bx bxs-circle text-indigo font-size-10 me-2" /><span>{props.t("Carephone")}</span></>
          case "carephone_accessory": return <><i className="bx bxs-circle text-indigo font-size-10 me-2" /><span>{props.t("Carephone accessory")}</span></>
          case "tracker_device": return <><i className="bx bxs-circle text-success font-size-10 me-2" /><span>{props.t("Tracker")}</span></>
          case "tracker_accessory": return <><i className="bx bxs-circle text-success font-size-10 me-2" /><span>{props.t("Tracker accessory")}</span></>
          case "wristwatch_device": return <><i className="bx bxs-circle text-success font-size-10 me-2" /><span>{props.t("Safety wristwatch")}</span></>
          case "wristwatch_accessory": return <><i className="bx bxs-circle text-success font-size-10 me-2" /><span>{props.t("Safety wristwatch accessory")}</span></>
          case "home_gw": return <><i className="bx bxs-circle text-tertiary font-size-10 me-2" /><span>{props.t("Home gateway")}</span></>
          case "home_set": return <><i className="bx bxs-circle text-tertiary font-size-10 me-2" /><span>{props.t("At home set")}</span></>
          case "home_node": return <><i className="bx bxs-circle text-tertiary font-size-10 me-2" /><span>{props.t("Home sensor")}</span></>
          case "home_accessory": return <><i className="bx bxs-circle text-tertiary font-size-10 me-2" /><span>{props.t("Home accessory")}</span></>
          case "smartlock_device": return <><i className="bx bxs-circle text-pink font-size-10 me-2" /><span>{props.t("Smartlock")}</span></>
          case "smartlock_accessory": return <><i className="bx bxs-circle text-pink font-size-10 me-2" /><span>{props.t("Smartlock accessory")}</span></>
          case "video_device": return <><i className="bx bxs-circle text-primary font-size-10 me-2" /><span>{props.t("Video device")}</span></>
          case "video_accessory": return <><i className="bx bxs-circle text-primary font-size-10 me-2" /><span>{props.t("Video accessory")}</span></>
          case "batteries": return <><i className="bx bxs-circle text-teal font-size-10 me-2" /><span>{props.t("Batteries")}</span></>
          case "other": return <><i className="bx bxs-circle text-teal font-size-10 me-2" /><span>{props.t("Other")}</span></>
          default: return cell
        }
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "info",
      title: props.t("Information"),
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "actions",
      align: "center",
      render: editFormatter,
      width: "60px",
      ellipsis: true,
    },
  ]

  function editFormatter(cell, row) {
    return (
      <div className="d-inline-flex">
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => {
            setReturnsItems(prev =>
              prev.filter(item => item.id !== row.id)
            )
            setDeliveryItems(prev =>
              prev.filter(item => item.id !== row.id)
            )
          }}
        >
          <i className="mdi mdi-close-thick" />
        </Button>
      </div>
    )
  }

  const getOrgSuggestions = (searchText) => {
    if (!searchText) return orgOptions
    let filteredUsers = orgOptions.filter(u => u.value.toLowerCase().includes(searchText.toLowerCase()))
    return filteredUsers
  }

  // Return content for selected item type
  const renderContent = (validationObj) => {
    switch (validationObj?.values.type) {
      case 'carephone_main':
        return (
          <SelectCarephone
            onChangeObject={setSelectedCarephone}
            selection={selectedCarephone?.value || null}
            multiple={false}
            clearable={true}
          />
        )
      case "tracker_device":
      case "wristwatch_device":
        return (
          <SelectTracker
            onChangeObject={setSelectedTracker}
            selection={selectedTracker?.value || null}
            multiple={false}
            clearable={true}
          />
        )
      case "video_device":
        return (
          <SelectVideoDevice
            onChangeObject={setSelectedVideoDevice}
            selection={selectedVideoDevice?.value || null}
            multiple={false}
            clearable={true}
          />
        )
      case "home_gw":
      case "home_set":
        return (
          <SelectGateway
            onChangeObject={setSelectedGateway}
            selection={selectedGateway?.value || null}
            multiple={false}
            clearable={true}
          />
        )
      case "smartlock_device":
        return (
          <SelectPindora
            onChangeObject={setSelectedSmartlock}
            selection={selectedSmartlock?.value || null}
            multiple={false}
            clearable={true}
          />
        )
      default:
        return (
          <AntdInput
            allowClear
            placeholder={props.t("Information")}
            value={validationObj?.values?.info}
            onChange={(v) => { validationObj?.setFieldValue("info", v.target.value) }}
          />
        );
    }
  };

  return (
    <Drawer
      title={(
        <>
          <Button
            className="compact-button ms-2"
            color="success"
            disabled={loading || saving || deleting || !data}
            onClick={() => {
              validation.submitForm()
            }}
          >
            <i className="bx bxs-save me-2 d-none d-sm-inline" />
            {props.t("Save changes")}
          </Button>
        </>
      )}
      extra={<span className="h5 font-size-16 font-weight-semibold">{"ID: " + props.id}</span>}
      width={"80%"}
      placement="right"
      maskClosable={false}
      keyboard={false}
      onClose={() => {
        props.setShowDrawer(false)
      }}
      open={props.showDrawer}
      zIndex={1003}
    >
      <>
        <Row className="mb-3" style={{ maxWidth: "1344px" }}>
          <Col></Col>
          <Col className="col-auto">
            <Popconfirm
              title={props.t("UnsavedChangesWarning")}
              open={refreshConfirm}
              okType="primary"
              okText={props.t("Refresh")}
              cancelText={props.t("Cancel")}
              onConfirm={() => {
                getData()
                setRefreshConfirm(false)
              }}
              onCancel={() => {
                setRefreshConfirm(false)
              }}
            >
              <Button
                className="compact-button"
                color="primary"
                outline
                disabled={loading || saving || deleting}
                onClick={() => {
                  setRefreshConfirm(true)
                }}
              >
                <i className="mdi mdi-refresh d-none d-sm-inline" />
              </Button>
            </Popconfirm>
            <Popconfirm
              title={props.t("Confirm action")}
              open={deleteConfirm}
              okType="danger"
              okText={props.t("Delete")}
              cancelText={props.t("Cancel")}
              onConfirm={() => {
                deleteEntry()
                setDeleteConfirm(false)
              }}
              onCancel={() => {
                setDeleteConfirm(false)
              }}
            >
              <Button
                className="compact-button ms-3"
                color="danger"
                outline
                disabled={loading || saving || deleting || !data}
                onClick={() => {
                  setDeleteConfirm(true)
                }}
              >
                <i className=" mdi mdi-trash-can me-2 d-none d-sm-inline" />
                {props.t("Delete")}
              </Button>
            </Popconfirm>
          </Col>
        </Row>

        {!loading && data && <>
          <Card style={{ maxWidth: "1320px" }}>
            <CardTitle
              className="mb-0 py-2 px-2 text-center card-title-blue"
            >
              {props.t("Information")}
            </CardTitle>
            <CardBody className="pb-5 pt-4" /*style={{ borderLeft: "4px solid #add2ea" }}*/>
              <Row xs={1} md={2}>
                <Col xs={12} md={7}>
                  <Row xs={1} lg={2}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Organization")}</Label>
                        <AutoComplete
                          placeholder={props.t("Organization")}
                          style={{ minWidth: 180, width: "100%" }}
                          options={suggestions}
                          defaultActiveFirstOption={false}
                          value={validation.values.organization}
                          onChange={(v) => { validation.setFieldValue("organization", v) }}
                          onSearch={(text) => setSuggestions(getOrgSuggestions(text))}
                        >
                          <AntdInput
                            allowClear
                          />
                        </AutoComplete>
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Customer")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("Customer")}
                          value={validation.values.customer}
                          onChange={(v) => { validation.setFieldValue("customer", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Contact")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("Contact")}
                          value={validation.values.contacts}
                          onChange={(v) => { validation.setFieldValue("contacts", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label>{props.t("Source")}</Label>
                          </Col>
                          <Col className="col-auto">
                            {/*data?.source_type == 1 && data?.source_text?.length > 0 && (
                            <a href={"https://support.suvanto.care/scp/tickets.php?id=" + data.source_text} target="_blank" rel="noopener noreferrer">{props.t("Ticket")}</a>
                          )*/}
                            {data?.source_type == 1 && data?.source_text?.length > 0 && (
                              <a href={"https://support.suvanto.care/scp/tickets.php?a=search&search-type=typeahead&query=" + data.source_text + "&number=" + data.source_text} target="_blank" rel="noopener noreferrer">{props.t("Ticket")}</a>
                            )}

                            {data?.source_type == 2 && data?.source_text?.length > 0 && (
                              <a href={"/sales/orders?id=" + data.source_text} target="_blank" rel="noopener noreferrer">{props.t("OrderAlt")}</a>
                            )}
                          </Col>
                        </Row>
                        <AntdInput
                          allowClear
                          placeholder={validation.values.source_type == 1 ? ("#" + props.t("Number")) : (validation.values.source_type == 2 ? props.t("Order number") : "")}
                          value={validation.values.source_text}
                          onChange={(v) => { validation.setFieldValue("source_text", v.target.value) }}
                          addonBefore={
                            <Select
                              value={validation.values.source_type}
                              style={{ minWidth: "80px" }}
                              popupMatchSelectWidth={false}
                              onChange={v => {
                                validation.setFieldValue("source_type", v)
                              }}
                            >
                              <Select.Option value={1}>{props.t("Ticket")}</Select.Option>
                              <Select.Option value={2}>{props.t("OrderAlt")}</Select.Option>
                              <Select.Option value={0}>{props.t("Other")}</Select.Option>
                            </Select>
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="d-grid">
                        <Label>{props.t("Informed at")}</Label>
                        <DatePicker
                          showNow
                          allowClear={false}
                          onChange={(v) => { validation.setFieldValue("informed_at", v) }}
                          format="DD.MM.YYYY"
                          value={validation.values.informed_at}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Highlighted row")}</Label>
                        <Select
                          value={validation.values.highlight}
                          onChange={(v) => { validation.setFieldValue("highlight", v) }}
                          options={[
                            { label: props.t("No"), value: 0 },
                            { label: props.t("Returns") + " (" + props.t("Red") + ")", value: 1 },
                            { label: props.t("Slow-up") + " (" + props.t("Orange") + ")", value: 2 },
                            { label: props.t("Other") + " (" + props.t("Purple") + ")", value: 3 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    {validation.values.highlight > 0 &&
                      <Col lg={12}>
                        <FormGroup>
                          <Label>{props.t("Highlight reason")}</Label>
                          <TextArea
                            allowClear
                            autoSize={{ minRows: 1, maxRows: 12 }}
                            placeholder={props.t("Highlight reason")}
                            value={validation.values.highlight_reason}
                            onChange={(v) => { validation.setFieldValue("highlight_reason", v.target.value) }}
                          />
                        </FormGroup>
                      </Col>
                    }
                  </Row>
                </Col>
                <Col xs={12} md={5}>
                  <FormGroup>
                    <Label>{props.t("Additional info")}</Label>
                    <TextArea
                      allowClear
                      autoSize={{ minRows: 5, maxRows: 12 }}
                      placeholder={props.t("Additional info")}
                      value={validation.values.additional_info}
                      onChange={(v) => { validation.setFieldValue("additional_info", v.target.value) }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <Card style={{ maxWidth: "1320px" }}>
            <CardTitle
              className="mt-0 mb-0 py-2 px-2 text-center card-title-yellow"
            >
              {props.t("Returns")}
            </CardTitle>
            <CardBody className="pb-5 pt-4" /*style={{ borderLeft: "4px solid #e9d88c" }}*/>
              <Row xs={1} md={2}>
                <Col xs={12} md={7}>
                  <Row xs={1} lg={2}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Returns status")}</Label>
                        <Select
                          value={validation.values.returns_status}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("returns_status", v) }}
                          options={[
                            { label: props.t("No returns"), value: 0 },
                            { label: props.t("Returning"), value: 1 },
                            { label: props.t("Handled"), value: 2 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Tracking code")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("MH...")}
                          value={validation.values.returns_tracking_code}
                          onChange={(v) => { validation.setFieldValue("returns_tracking_code", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Reason for return")}</Label>
                        <Select
                          allowClear
                          placeholder={props.t("Select")}
                          value={validation.values.returns_reason}
                          onChange={(v) => { validation.setFieldValue("returns_reason", v) }}
                          options={reasonOptions}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="d-grid">
                        <Label>{props.t("Handled at")}</Label>
                        <DatePicker
                          showNow
                          allowClear
                          onChange={(v) => { validation.setFieldValue("returns_handled_at", v) }}
                          format="DD.MM.YYYY"
                          value={validation.values.returns_handled_at}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Missing items")}</Label>
                        <Select
                          value={validation.values.returns_missing}
                          onChange={(v) => { validation.setFieldValue("returns_missing", v) }}
                          options={[
                            { label: props.t("No"), value: 0 },
                            { label: props.t("Yes"), value: 1 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Invoicing")}</Label>
                        <Select
                          allowClear
                          placeholder={props.t("Select")}
                          value={validation.values.invoicing}
                          onChange={(v) => { validation.setFieldValue("invoicing", v) }}
                          options={[
                            { label: props.t("No"), value: 1 },
                            { label: props.t("Yes"), value: 2 },
                            { label: props.t("Requires investigation"), value: 4 },
                            { label: props.t("Invoiced"), value: 3 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Invoiced items")}</Label>
                        <TextArea
                          allowClear
                          autoSize={{ minRows: 1, maxRows: 6 }}
                          value={validation.values.invoicing_content}
                          onChange={(v) => { validation.setFieldValue("invoicing_content", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="d-grid">
                        <Label>{props.t("Invoicing ends at")}</Label>
                        <DatePicker
                          showNow
                          allowClear
                          onChange={(v) => { validation.setFieldValue("invoicing_ends_at", v) }}
                          format="DD.MM.YYYY"
                          value={validation.values.invoicing_ends_at}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md={5}>
                  <FormGroup>
                    <Label>{props.t("Additional info")}</Label>
                    <TextArea
                      allowClear
                      autoSize={{ minRows: 5, maxRows: 12 }}
                      placeholder={props.t("Additional info")}
                      value={validation.values.returns_additional}
                      onChange={(v) => { validation.setFieldValue("returns_additional", v.target.value) }}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Divider
                orientation="left"
                className="font-size-14"
                orientationMargin="0"
                style={{ textDecoration: "underline" }}
              >
                {props.t("Returning items").toUpperCase()}
              </Divider>
              <Row>
                <Col style={{ maxWidth: "300px" }} className="mb-2">
                  <Select
                    value={returnedItemsValidation.values.type}
                    placeholder={props.t("Type")}
                    onChange={(v) => {
                      returnedItemsValidation.setFieldValue("type", v)
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={itemOptions}
                    style={{ width: 100 + "%" }}
                  />
                </Col>
                <Col style={{ maxWidth: "450px" }} className="mb-2">
                  {renderContent(returnedItemsValidation)}
                </Col>
                <Col className="col-auto">
                  <Button
                    className="compact-button"
                    color="success"
                    disabled={loading || saving || !returnedItemsValidation.values.type}
                    onClick={() => { returnedItemsValidation.submitForm() }}
                  >
                    {props.t("Add")}
                  </Button>
                </Col>
              </Row>
              <AntdTable
                className="narrow-header mt-3"
                bordered
                size="small"
                rowKey={"id"}
                columns={devicesColumns}
                dataSource={returnsItems}
                pagination={false}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: tableDefaultHeight,
                }}
              />
            </CardBody>
          </Card>
          <Card className="mb-5" style={{ maxWidth: "1320px" }}>
            <CardTitle
              className="mt-0 mb-0 py-2 px-2 text-center card-title-green"
            >
              {props.t("Deliveries")}
            </CardTitle>
            <CardBody className="pb-5 pt-4" /*style={{ borderLeft: "4px solid #b8dfca" }}*/>
              <Row xs={1} md={2}>
                <Col xs={12} md={7}>
                  <Row xs={1} lg={2}>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Delivery status")}</Label>
                        <Select
                          value={validation.values.delivery_status}
                          placeholder={props.t("Choose")}
                          onChange={(v) => { validation.setFieldValue("delivery_status", v) }}
                          options={[
                            { label: props.t("No delivery"), value: 0 },
                            { label: props.t("Waiting"), value: 1 },
                            { label: props.t("Sent"), value: 2 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{props.t("Tracking code")}</Label>
                        <AntdInput
                          allowClear
                          placeholder={props.t("MH...")}
                          value={validation.values.delivery_tracking_code}
                          onChange={(v) => { validation.setFieldValue("delivery_tracking_code", v.target.value) }}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="d-grid">
                        <Label>{props.t("Sent")}</Label>
                        <DatePicker
                          showNow
                          allowClear
                          onChange={(v) => { validation.setFieldValue("delivery_sent_at", v) }}
                          format="DD.MM.YYYY"
                          value={validation.values.delivery_sent_at}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>{"Elisa " + props.t("Device register").toLowerCase()}</Label>
                        <Select
                          allowClear
                          placeholder={props.t("Select")}
                          value={validation.values.elisa_register}
                          onChange={(v) => { validation.setFieldValue("elisa_register", v) }}
                          options={[
                            { label: props.t("No need to notify"), value: 1 },
                            { label: props.t("Requires notification"), value: 2 },
                            { label: props.t("Notified"), value: 3 },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md={5}>
                  <FormGroup>
                    <Label>{props.t("Additional info")}</Label>
                    <TextArea
                      allowClear
                      autoSize={{ minRows: 5, maxRows: 12 }}
                      placeholder={props.t("Additional info")}
                      value={validation.values.delivery_additional}
                      onChange={(v) => { validation.setFieldValue("delivery_additional", v.target.value) }}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Divider
                orientation="left"
                className="font-size-14"
                orientationMargin="0"
                style={{ textDecoration: "underline" }}
              >
                {props.t("Outgoing items").toUpperCase()}
              </Divider>
              <Row>
                <Col style={{ maxWidth: "300px" }} className="mb-2">
                  <Select
                    value={deliveryItemsValidation.values.type}
                    placeholder={props.t("Type")}
                    onChange={(v) => {
                      deliveryItemsValidation.setFieldValue("type", v)
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={itemOptions}
                    style={{ width: 100 + "%" }}
                  />
                </Col>
                <Col style={{ maxWidth: "450px" }} className="mb-2">
                  {renderContent(deliveryItemsValidation)}
                </Col>
                <Col className="col-auto">
                  <Button
                    className="compact-button"
                    color="success"
                    disabled={loading || saving || !deliveryItemsValidation.values.type}
                    onClick={() => { deliveryItemsValidation.submitForm() }}
                  >
                    {props.t("Add")}
                  </Button>
                </Col>
              </Row>

              <AntdTable
                className="narrow-header mt-3"
                bordered
                size="small"
                rowKey={"id"}
                columns={devicesColumns}
                dataSource={deliveryItems}
                pagination={false}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: tableDefaultHeight,
                }}
              />
            </CardBody>
          </Card>
        </>}

        {loading &&
          <Card style={{ maxWidth: "1320px" }}>
            <div className="text-center my-5">
              <Lottie
                options={getLottieLoadingOptions()}
                height={100}
                width={100}
              ></Lottie>
            </div>
          </Card>
        }

        {!loading && !data &&
          <Card style={{ maxWidth: "1320px" }}>
            <div className="text-center my-5">
              <Lottie
                options={getLottieNothingFound()}
                height={100}
                width={100}
              ></Lottie>
            </div>
          </Card>
        }
      </>
    </Drawer>
  )
}

ReturnsForm.propTypes = {
  t: PropTypes.any,
  id: PropTypes.any,
  showDrawer: PropTypes.bool,
  setShowDrawer: PropTypes.func
}

export default withTranslation()(ReturnsForm)